import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonModal,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import {
  alertCircleOutline,
  checkmarkCircleOutline,
  closeOutline,
  removeCircleOutline,
} from 'ionicons/icons';
import { useState } from 'react';

export type ImportQueueItem = {
  index: number;
  title: string;
  meta: string;
  status: 'ready' | 'review' | 'skipped';
};

type Props = {
  documentLabel: string;
  items: ImportQueueItem[];
  currentIndex: number;
  onSelect: (index: number) => void;
};

const statusIcon = {
  ready: checkmarkCircleOutline,
  review: alertCircleOutline,
  skipped: removeCircleOutline,
} as const;

function QueueList(props: Props & { close?: () => void }): JSX.Element {
  return (
    <div className="te-import-queue__list">
      {props.items.map((item) => (
        <button
          type="button"
          key={item.index}
          className={`te-import-queue__item${item.index === props.currentIndex ? ' is-active' : ''}`}
          aria-current={item.index === props.currentIndex ? 'step' : undefined}
          onClick={() => {
            props.onSelect(item.index);
            props.close?.();
          }}
        >
          <span className="te-import-queue__index">{item.index + 1}</span>
          <span className="te-import-queue__copy">
            <strong>{item.title}</strong>
            <small>{item.meta}</small>
          </span>
          <IonIcon
            icon={statusIcon[item.status]}
            className={`te-import-queue__state te-import-queue__state--${item.status}`}
            aria-hidden="true"
          />
        </button>
      ))}
    </div>
  );
}

export function ImportReviewQueue(props: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const ready = props.items.filter((item) => item.status === 'ready').length;
  const review = props.items.filter((item) => item.status === 'review').length;

  return (
    <>
      <aside className="te-import-queue" aria-label="Articoli del documento">
        <div className="te-import-queue__head">
          <div className="te-import-queue__eyebrow">{props.documentLabel}</div>
          <h2>{props.items.length} articoli</h2>
          <div className="te-import-queue__summary">
            <span><strong>{ready}</strong> pronti</span>
            <span><strong>{review}</strong> da verificare</span>
          </div>
        </div>
        <QueueList {...props} />
      </aside>

      <div className="te-import-mobile-queue">
        <div>
          <strong>Articolo {props.currentIndex + 1} di {props.items.length}</strong>
          <small>{review} da verificare</small>
        </div>
        <IonButton fill="clear" onClick={() => setOpen(true)}>
          Vedi elenco
        </IonButton>
      </div>

      <IonModal isOpen={open} onDidDismiss={() => setOpen(false)} initialBreakpoint={0.78} breakpoints={[0, 0.78, 1]}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>Articoli del documento</IonTitle>
            <IonButtons slot="end">
              <IonButton aria-label="Chiudi elenco articoli" onClick={() => setOpen(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <QueueList {...props} close={() => setOpen(false)} />
        </IonContent>
      </IonModal>
    </>
  );
}
